import { createContext, useContext, useEffect, useMemo, useState } from "react";

const SiteContext = createContext(null);

// Base currency is CAD (Waterloo, ON listing). Conversion rates are approximate, for display only.
const RATES = {
  CAD: { rate: 1, symbol: "$", locale: "en-CA" },
  USD: { rate: 0.73, symbol: "US$", locale: "en-US" },
  EUR: { rate: 0.67, symbol: "€", locale: "de-DE" },
};

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BASE}/api`;

export function SiteProvider({ children }) {
  const [currency, setCurrency] = useState("CAD");
  const [media, setMedia] = useState([]);
  const [mediaReady, setMediaReady] = useState(false);
  const [floorPlanLevel, setFloorPlanLevel] = useState(null);
  const [flyoutOpen, setFlyoutOpen] = useState(false);

  async function refreshMedia() {
    try {
      const res = await fetch(`${API}/media`);
      if (res.ok) {
        const rows = await res.json();
        // Make photo URLs absolute (backend origin) so images resolve even when
        // the site is viewed from a custom domain or embedded on a sub-page.
        setMedia(
          rows.map((m) =>
            m.url && m.url.startsWith("/") ? { ...m, url: `${BASE}${m.url}` } : m
          )
        );
      }
    } catch {
      setMedia([]);
    } finally {
      setMediaReady(true);
    }
  }

  useEffect(() => {
    refreshMedia();
  }, []);

  const value = useMemo(() => {
    const heroMedia = media.find((m) => m.role === "hero");
    return {
      currency,
      setCurrency,
      currencies: Object.keys(RATES),
      formatPrice: (cad) => {
        const c = RATES[currency];
        const v = currency === "CAD" ? cad : Math.round((cad * c.rate) / 1000) * 1000;
        return (
          c.symbol +
          new Intl.NumberFormat(c.locale, { maximumFractionDigits: 0 }).format(v)
        );
      },
      media,
      mediaReady,
      refreshMedia,
      heroUrl: heroMedia ? heroMedia.url : null,
      floorPlanLevel,
      openFloorPlans: (levelId = "entrance") => setFloorPlanLevel({ id: levelId, n: Date.now() }),
      flyoutOpen,
      setFlyoutOpen,
    };
  }, [currency, media, mediaReady, floorPlanLevel, flyoutOpen]);

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export const useSite = () => useContext(SiteContext);
